import './manager.component.less'
import { readTheme, applyTheme, clearTheme } from 'common/theme'

/**
 * Whether a scene is the one on screen in the manager. The scene page, its
 * calendar, its Compose view and its configuration are sibling states that all
 * take the scene id, so the highlight in the navbar must follow the id, not
 * one state name.
 */
function sceneIsActive (stateName, params, sceneId) {
  return typeof stateName === 'string' &&
    stateName.startsWith('manager.scene') &&
    parseInt(params && params.id) === sceneId
}

/**
 * Whether the browser asks for a dark page. A browser without matchMedia, and
 * jsdom in a spec, answer no.
 */
function prefersDark ($window) {
  return Boolean($window.matchMedia && $window.matchMedia('(prefers-color-scheme: dark)').matches)
}

class ManagerController {
  constructor ($rootScope, $scope, $transitions, $state, ClientService, SceneService, PrimusService, $window) {
    'ngInject'

    this._$window = $window

    // The theme of the manager. The shell writes the attribute on the html
    // element, because every page of the manager stands inside the shell.
    this.theme = readTheme($window.localStorage)
    applyTheme(this.theme, this._themeOptions())

    this.clientId = ClientService.id

    this.Scenes = SceneService.getAll()

    this.unsubscribe = SceneService.subscribe(state => {
      this.Scenes = state.Scenes
    })

    this.version = ClientService.version

    this.State = $state

    // Menu is initially closed
    this.isMenuCollapsed = true
    this.isDropdownOpen = false

    // Connection status tracking
    this.connectionStatus = PrimusService.getConnectionStatus()
    this._unsubscribeConnectionStatus = PrimusService.onConnectionStatusChange(status => {
      this.connectionStatus = status
    })

    $transitions.onSuccess({}, () => {
      this.isMenuCollapsed = true
      this.isDropdownOpen = false
    })

    // Stats for any uploads in progress
    this.uploadStats = {}
    this._uploadStatsListener = $rootScope.$on('asset-upload-service:stats', ($event, stats) => {
      this.uploadStats = stats
    })

    $scope.$on('$destroy', () => {
      this.unsubscribe()
      this._uploadStatsListener()
      this._unsubscribeConnectionStatus()
    })

    this.isActive = (sceneId) => sceneIsActive($state.current.name, $state.params, sceneId)

    this.toggleDropdown = ($event) => {
      $event.preventDefault()
      $event.stopPropagation()
      this.isDropdownOpen = !this.isDropdownOpen
    }

    // Helper methods for connection status display
    this.getConnectionStatusIcon = () => {
      // Simplify to just online/offline for user
      const isOnline = this.connectionStatus === 'connected'
      return isOnline ? 'fa-circle text-success' : 'fa-circle text-danger'
    }

    this.getConnectionStatusTitle = () => {
      // Still show detailed status in tooltip
      switch (this.connectionStatus) {
        case 'connected': return 'Online - Connected to server'
        case 'connecting': return 'Connecting - Attempting to reconnect'
        case 'disconnected': return 'Offline - No connection to server'
        case 'mock': return 'Offline Mode - Using mock connection'
        default: return 'Unknown connection status'
      }
    }
  }

  /** The html element of the page. The theme is one attribute on it. */
  _themeRoot () {
    const page = this._$window.document

    return page && page.documentElement
  }

  /** The parts of the browser that the theme needs. */
  _themeOptions () {
    return {
      root: this._themeRoot(),
      storage: this._$window.localStorage,
      prefersDark: prefersDark(this._$window)
    }
  }

  /** The operator picks one of the three themes in the menu. */
  setTheme (name) {
    this.theme = name
    applyTheme(name, this._themeOptions())
  }

  $onDestroy () {
    // The player pages do not read the tokens of the manager, so the shell
    // takes the attribute with it.
    clearTheme({ root: this._themeRoot() })
  }
}

export { sceneIsActive, ManagerController }

export default {
  controller: ManagerController,
  template: require('./manager.component.html').default
}
